import { useMemo, useState } from 'react';
import type { Organization } from '@stfors/shared-types';
import { useOrganizations, organizationDisplayName } from '../api/queries/organizations';
import './OrgPicker.css';

interface OrgPickerProps {
  /** Currently selected organization id, or null. */
  value: number | null;
  onChange: (orgId: number | null, org: Organization | null) => void;
  disabled?: boolean;
}

/**
 * Typeahead picker over the companies list (server-side ?q= search), used to
 * link an existing individual to a company. Modeled on <VendorPicker>.
 */
export function OrgPicker({ value, onChange, disabled = false }: OrgPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const orgsQ = useOrganizations(query);
  const orgs = orgsQ.data?.organizations ?? [];

  const selected = useMemo(() => orgs.find((o) => o.id === value) ?? null, [orgs, value]);
  const inputValue = open ? query : selected ? organizationDisplayName(selected) : '';

  const choose = (o: Organization | null) => {
    onChange(o?.id ?? null, o);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={`op-root${disabled ? ' op-disabled' : ''}`}>
      <div className="op-input-wrap">
        <input
          type="text"
          className="form-input op-input"
          value={inputValue}
          placeholder={orgsQ.isPending ? 'Loading…' : 'Search companies…'}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          disabled={disabled}
          aria-label="Company"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {selected && !open && (
          <button
            type="button"
            className="op-clear"
            onClick={() => choose(null)}
            aria-label="Clear company"
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="op-options" role="listbox">
          {orgs.length === 0 && (
            <li className="op-empty">
              {orgsQ.isPending ? 'Loading…' : `No companies match "${query}"`}
            </li>
          )}
          {orgs.map((o) => (
            <li key={o.id} role="option" aria-selected={o.id === value}>
              <button
                type="button"
                className="op-option"
                onMouseDown={(e) => e.preventDefault() /* keep focus */}
                onClick={() => choose(o)}
              >
                <span className="op-option-name">{organizationDisplayName(o)}</span>
                {o.dba_name && o.dba_name.trim() && o.dba_name.trim() !== o.name.trim() && (
                  <span className="op-option-dba"> legal: {o.name}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
